import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { queryKeys } from "@/lib/queryKeys";
import type { Json } from "@/integrations/supabase/types";
import type { Room } from "@/types/checklist";
import { getTaskText } from "@/types/checklist";

/** Task label for imported before/after photos (grouped per room on the report). */
export const IMPORT_TURNOVER_PHOTO_TASK_LABEL = "Cleaning photos";

/** Room used when the host skips per-photo room assignment. */
export const IMPORT_UNASSIGNED_ROOM_LABEL = "General";

/** Parallel storage uploads — balances speed vs browser connection limits. */
const UPLOAD_CONCURRENCY = 6;

/** Rows per task_submission_photos insert. */
const DB_INSERT_BATCH_SIZE = 50;

export interface AssignedImportPhoto {
  file: File;
  roomName: string;
  phase: "before" | "after";
  isIssue?: boolean;
  issueDescription?: string;
}

export type ImportUploadProgress = {
  completed: number;
  total: number;
  phase: "uploading" | "saving";
};

interface MediaUploadJob {
  photo: AssignedImportPhoto;
  roomName: string;
  type: "before" | "after";
  displayOrder: number;
  filePath: string;
}

function mediaContentType(file: File): string {
  if (file.type) return file.type;
  const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
  if (["mp4", "webm", "mov", "m4v", "mkv", "mpeg", "mpg", "3gp", "ogg", "ogv"].includes(ext)) {
    return ext === "mov" ? "video/quicktime" : `video/${ext === "mpg" ? "mpeg" : ext}`;
  }
  return ext === "jpg" ? "image/jpeg" : `image/${ext}`;
}

function chunkArray<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

async function runWithConcurrency<T, R>(
  items: T[],
  concurrency: number,
  worker: (item: T, index: number) => Promise<R>,
  onItemDone?: (completed: number, total: number) => void,
): Promise<R[]> {
  if (items.length === 0) return [];
  const results = new Array<R>(items.length);
  let nextIndex = 0;
  let completed = 0;

  async function runWorker() {
    while (nextIndex < items.length) {
      const current = nextIndex++;
      results[current] = await worker(items[current], current);
      completed += 1;
      onItemDone?.(completed, items.length);
    }
  }

  const poolSize = Math.min(concurrency, items.length);
  await Promise.all(Array.from({ length: poolSize }, () => runWorker()));
  return results;
}

function buildMediaUploadJobs(submissionId: string, photos: AssignedImportPhoto[]): MediaUploadJob[] {
  const beforeAfterByRoom = new Map<string, { before: AssignedImportPhoto[]; after: AssignedImportPhoto[] }>();

  for (const photo of photos) {
    const roomName = photo.roomName.trim();
    const bucket = beforeAfterByRoom.get(roomName) ?? { before: [], after: [] };
    if (photo.phase === "before") bucket.before.push(photo);
    else bucket.after.push(photo);
    beforeAfterByRoom.set(roomName, bucket);
  }

  const jobs: MediaUploadJob[] = [];
  let jobIndex = 0;

  for (const [roomName, bucket] of beforeAfterByRoom) {
    const roomSlug = roomName.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/, "");
    const taskSlug = IMPORT_TURNOVER_PHOTO_TASK_LABEL.toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/-+$/, "")
      .slice(0, 30);

    for (let i = 0; i < bucket.before.length; i++) {
      const photo = bucket.before[i];
      const fileExt = photo.file.name.split(".").pop()?.toLowerCase() || "jpg";
      jobs.push({
        photo,
        roomName,
        type: "before",
        displayOrder: i,
        filePath: `${submissionId}/${roomSlug}/${taskSlug}-before-${jobIndex++}.${fileExt}`,
      });
    }
    for (let i = 0; i < bucket.after.length; i++) {
      const photo = bucket.after[i];
      const fileExt = photo.file.name.split(".").pop()?.toLowerCase() || "jpg";
      jobs.push({
        photo,
        roomName,
        type: "after",
        displayOrder: i,
        filePath: `${submissionId}/${roomSlug}/${taskSlug}-after-${jobIndex++}.${fileExt}`,
      });
    }
  }

  return jobs;
}

async function uploadJobToStorage(job: MediaUploadJob): Promise<string> {
  const { error: uploadError } = await supabase.storage
    .from("cleaning-photos")
    .upload(job.filePath, job.photo.file, {
      contentType: mediaContentType(job.photo.file),
      upsert: true,
    });
  if (uploadError) throw uploadError;

  const { data: urlData } = supabase.storage.from("cleaning-photos").getPublicUrl(job.filePath);
  return urlData.publicUrl;
}

function buildTasksStatusFromRooms(rooms: Room[], completed: boolean) {
  return rooms.flatMap((room) =>
    (room.tasks ?? []).map((task) => ({
      room_name: room.name,
      task_text: getTaskText(task),
      completed,
    })),
  );
}

export function useImportTurnoverPhotos() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      propertyId,
      taskId,
      checklistId,
      checklistRooms,
      existingSubmissionId,
      assignedPhotos,
      notes,
      markTaskCompleted,
      onProgress,
    }: {
      propertyId: string;
      taskId: string;
      checklistId?: string | null;
      checklistRooms?: Room[];
      existingSubmissionId?: string | null;
      assignedPhotos: AssignedImportPhoto[];
      notes?: string;
      markTaskCompleted?: boolean;
      onProgress?: (progress: ImportUploadProgress) => void;
    }) => {
      if (!user?.id) throw new Error("Not authenticated");

      if (assignedPhotos.length === 0) throw new Error("Add at least one photo");

      const photos = assignedPhotos.map((p) => ({
        ...p,
        roomName: p.roomName.trim() || IMPORT_UNASSIGNED_ROOM_LABEL,
        issueDescription:
          p.isIssue && !p.issueDescription?.trim()
            ? "Issue reported during turnover import."
            : p.issueDescription,
      }));

      const { data: task, error: taskErr } = await supabase
        .from("cleaning_tasks")
        .select("id, checklist_id, inventory_completed, completed_at, started_at, duration_minutes, status")
        .eq("id", taskId)
        .eq("property_id", propertyId)
        .single();
      if (taskErr || !task) throw new Error("Task not found");

      let submissionId = existingSubmissionId ?? null;

      if (!submissionId) {
        submissionId = crypto.randomUUID();
        const resolvedChecklistId = checklistId ?? task.checklist_id ?? null;
        const rooms = checklistRooms ?? [];
        const tasksStatus = markTaskCompleted ? buildTasksStatusFromRooms(rooms, true) : [];
        const completionPercentage =
          markTaskCompleted && tasksStatus.length > 0 ? 100 : null;

        const { error: insertErr } = await supabase.from("cleaning_submissions").insert({
          id: submissionId,
          property_id: propertyId,
          cleaner_id: null as unknown as string,
          submitted_by_user_id: user.id,
          checklist_id: resolvedChecklistId,
          cleaning_task_id: taskId,
          notes: notes?.trim() || "Turnover photos imported by host.",
          issues: [] as unknown as Json,
          photos: [] as unknown as Json,
          tasks_status: tasksStatus as unknown as Json,
          completion_percentage: completionPercentage,
        });
        if (insertErr) throw insertErr;
      } else if (notes?.trim()) {
        const { error: noteErr } = await supabase
          .from("cleaning_submissions")
          .update({ notes: notes.trim() })
          .eq("id", submissionId);
        if (noteErr) throw noteErr;
      }

      const jobs = buildMediaUploadJobs(submissionId, photos);
      onProgress?.({ completed: 0, total: jobs.length, phase: "uploading" });

      const uploadResults = await runWithConcurrency(
        jobs,
        UPLOAD_CONCURRENCY,
        async (job) => {
          const publicUrl = await uploadJobToStorage(job);
          return { job, publicUrl };
        },
        (completed, total) => onProgress?.({ completed, total, phase: "uploading" }),
      );

      onProgress?.({ completed: 0, total: uploadResults.length, phase: "saving" });

      const photoRows = uploadResults.map(({ job, publicUrl }) => ({
        submission_id: submissionId!,
        room_name: job.roomName,
        task_text: IMPORT_TURNOVER_PHOTO_TASK_LABEL,
        photo_url: publicUrl,
        type: job.type,
        display_order: job.displayOrder,
      }));

      const issueEntries: { roomName: string; description: string; photoUrl: string }[] = [];
      for (const { job, publicUrl } of uploadResults) {
        if (job.photo.isIssue) {
          issueEntries.push({
            roomName: job.roomName,
            description: job.photo.issueDescription!.trim(),
            photoUrl: publicUrl,
          });
        }
      }

      let saved = 0;
      for (const batch of chunkArray(photoRows, DB_INSERT_BATCH_SIZE)) {
        const { error: dbError } = await supabase.from("task_submission_photos").insert(batch);
        if (dbError) throw dbError;
        saved += batch.length;
        onProgress?.({ completed: saved, total: photoRows.length, phase: "saving" });
      }

      if (issueEntries.length > 0) {
        let existingIssues: Json[] = [];
        if (existingSubmissionId) {
          const { data: row } = await supabase
            .from("cleaning_submissions")
            .select("issues")
            .eq("id", submissionId)
            .maybeSingle();
          if (Array.isArray(row?.issues)) {
            existingIssues = row.issues as Json[];
          }
        }

        const newIssues = issueEntries.map(
          (entry) =>
            ({
              room_name: entry.roomName,
              description: entry.description,
              photo_url: entry.photoUrl,
              severity: "medium",
            }) as Json,
        );

        const { error: issuesErr } = await supabase
          .from("cleaning_submissions")
          .update({ issues: [...existingIssues, ...newIssues] as unknown as Json })
          .eq("id", submissionId);
        if (issuesErr) throw issuesErr;
      }

      if (markTaskCompleted) {
        const now = new Date();
        const updateData: Record<string, unknown> = {
          status: "completed",
          checklist_completed: true,
        };
        if (!task.completed_at) updateData.completed_at = now.toISOString();
        if (task.started_at && task.duration_minutes === null) {
          updateData.duration_minutes = Math.ceil(
            (now.getTime() - new Date(task.started_at).getTime()) / 60000,
          );
        }

        const { error: updateErr } = await supabase
          .from("cleaning_tasks")
          .update(updateData)
          .eq("id", taskId);
        if (updateErr) throw updateErr;

        await supabase
          .rpc("snapshot_cleaning_task_fee_from_agreement", { p_task_id: taskId })
          .then(() => {})
          .catch(() => {});
      }

      return {
        propertyId,
        taskId,
        submissionId,
        photoCount: photos.length,
        issueCount: issueEntries.length,
      };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.submissions.byProperty(data.propertyId) });
      queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", data.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["task-submission-photos", data.submissionId] });
      queryClient.invalidateQueries({ queryKey: ["task-for-owner-team-runner", data.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["my-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["cleaning-submission-for-task", data.taskId] });
    },
  });
}
